'use client';

import React, { useState } from 'react';
import { Box, Typography } from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faFolderBookmark,
  faFolderOpen,
} from '@fortawesome/pro-solid-svg-icons';
import { SavedVolumeType } from 'src/hooks/savedVolume/types';
import VolumeItem from './VolumeItem';
import type { AccountFolder } from 'src/api/redux/accountFolders';

interface VolumeSectionProps {
  label: string;
  accordionIndex: number;
  openAccordion: number | null;
  onToggleAccordion: (index: number) => void;
  volumes: any[];
  folders: AccountFolder[];
  appliedSavedVolume: SavedVolumeType | null;
  showActions: boolean;
  location: string;
  // Folder helpers
  getFolderVolumes: (folder: AccountFolder) => any[];
  // DnD props
  isDraggingRef: React.MutableRefObject<boolean>;
  dragOverSection: 'personal' | 'company' | null;
  sectionKey: 'personal' | 'company';
  onSectionDragOver: (e: React.DragEvent, type: 'personal' | 'company') => void;
  onSectionDragLeave: (type: 'personal' | 'company') => void;
  onSectionDrop: (e: React.DragEvent, scope: 'PERSONAL' | 'COMPANY') => void;
  onFolderDrop: (e: React.DragEvent, folder: AccountFolder) => void;
  onDragStart: (e: React.DragEvent, volume: any) => void;
  onDragEnd: () => void;
  setDragOverSection: (section: 'personal' | 'company' | null) => void;
  // Callbacks
  onSelect: (volume: SavedVolumeType) => void;
  onEdit: (
    e: React.MouseEvent,
    volume: { id: number; label: string; volumeType?: string }
  ) => void;
  onDelete: (
    e: React.MouseEvent,
    volume: { id: number; label: string; volumeType?: 'cohort' | 'filterSet' }
  ) => void;
  onReactivate: (
    e: React.MouseEvent,
    volume: { id: number; label: string }
  ) => void;
  onManage: (e: React.MouseEvent, type: 'PERSONAL' | 'COMPANY') => void;
}

const buildSelectPayload = (volume: any): SavedVolumeType => {
  if (volume.volumeType === 'advancedSearch' || volume.volumeType === 'aiSearch') {
    return {
      id: volume.id,
      label: volume.label,
      status: volume.status,
      description: volume.description,
      volumeType: 'advancedSearch',
    };
  }
  if (volume.volumeType === 'filterSet') {
    return {
      id: volume.id,
      label: volume.label,
      type: volume.type || 'CODE',
      scope: volume.scope,
      owner: volume.owner,
      filters: volume.filters || [],
      created_at: volume.created_at || new Date().toISOString(),
      status: volume.status,
      volumeType: 'filterSet',
    };
  }
  return {
    id: volume.id,
    label: volume.label,
    status: volume.status,
    volumeType: 'cohort',
  };
};

const VolumeSection: React.FC<VolumeSectionProps> = ({
  label,
  accordionIndex,
  openAccordion,
  onToggleAccordion,
  volumes,
  folders,
  appliedSavedVolume,
  showActions,
  location,
  getFolderVolumes,
  isDraggingRef,
  dragOverSection,
  sectionKey,
  onSectionDragOver,
  onSectionDragLeave,
  onSectionDrop,
  onFolderDrop,
  onDragStart,
  onDragEnd,
  setDragOverSection,
  onSelect,
  onEdit,
  onDelete,
  onReactivate,
  onManage,
}) => {
  const [openFolders, setOpenFolders] = useState<Record<number, boolean>>({});
  const isOpen = openAccordion === accordionIndex;
  const scopeType = sectionKey === 'company' ? 'COMPANY' : 'PERSONAL';

  if (volumes.length === 0 && folders.length === 0) return null;

  const toggleFolder = (folderId: number) => {
    setOpenFolders((prev) => ({
      ...prev,
      [folderId]: !prev[folderId],
    }));
  };

  const renderFolder = (folder: AccountFolder) => {
    const isFolderOpen = openFolders[folder.id];
    const folderVolumes = getFolderVolumes(folder);

    return (
      <div key={folder.id} className="mb-2">
        <div
          className={`flex items-center hover:bg-neutral-100 ${
            isDraggingRef.current ? 'hover:bg-sky-200' : ''
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            e.currentTarget.classList.add('bg-sky-200');
          }}
          onDragLeave={(e) => {
            e.currentTarget.classList.remove('bg-sky-200');
          }}
          onDrop={(e) => {
            e.stopPropagation();
            e.currentTarget.classList.remove('bg-sky-200');
            onFolderDrop(e, folder);
          }}
          data-testid={`volume-folder-${folder.id}`}
        >
          <button
            type="button"
            className="flex items-center flex-1 px-6 py-2 cursor-pointer"
            onClick={() => toggleFolder(folder.id)}
          >
            <div className="w-4 h-4 flex items-center justify-center mr-2">
              <Icon
                icon={isFolderOpen ? faFolderOpen : faFolderBookmark}
                className="text-neutral-600"
              />
            </div>
            <Typography className="text-sm font-semibold text-neutral-800 flex-1 text-left">
              {folder.label}
            </Typography>
          </button>
        </div>

        {isFolderOpen && (
          <Box className="ml-6">
            {folderVolumes.map((volume) => (
              <VolumeItem
                key={`folder-${volume.volumeType}-${volume.id}`}
                volume={volume}
                isSelected={
                  appliedSavedVolume?.id === volume.id &&
                  appliedSavedVolume?.volumeType === volume.volumeType
                }
                subtitle={
                  volume.volumeType === 'advancedSearch' ||
                  volume.volumeType === 'aiSearch'
                    ? 'AI Search'
                    : volume.volumeType === 'filterSet'
                      ? 'All Claims'
                      : 'Encounters'
                }
                onSelect={() => onSelect(buildSelectPayload(volume))}
                onEdit={(e) =>
                  onEdit(e, { id: volume.id, label: volume.label, volumeType: volume.volumeType })
                }
                onDelete={(e) =>
                  onDelete(e, {
                    id: volume.id,
                    label: volume.label,
                    volumeType: volume.volumeType,
                  })
                }
                onReactivate={(e) =>
                  onReactivate(e, { id: volume.id, label: volume.label })
                }
                onDragStart={(e) => onDragStart(e, volume)}
                onDragEnd={onDragEnd}
              />
            ))}
          </Box>
        )}
      </div>
    );
  };

  return (
    <div className="border-t border-neutral-200">
      <div
        className="flex items-center px-4 py-2 hover:bg-neutral-100"
        data-testid={`volume-selector-${sectionKey}-accordion`}
      >
        <button
          type="button"
          className="flex items-center flex-1 cursor-pointer"
          onClick={() => onToggleAccordion(accordionIndex)}
        >
          <div className="w-3 h-3 flex items-center justify-center mr-2">
            <Icon
              icon={isOpen ? faChevronDown : faChevronRight}
              className="text-neutral-600 text-xs"
              fixedWidth
            />
          </div>
          <Typography className="text-sm font-semibold text-neutral-600">
            {label}
          </Typography>
        </button>

        {showActions && (
          <button
            type="button"
            className="ml-auto cursor-pointer"
            onClick={(e) => onManage(e, scopeType)}
          >
            <Typography className="text-xs text-text-primary">
              Manage
            </Typography>
          </button>
        )}
      </div>

      {isOpen && (
        <div
          className={`${
            location === 'discovery' ? '' : 'max-h-56'
          } overflow-y-auto ${
            dragOverSection === sectionKey ? 'bg-sky-50' : ''
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOverSection(sectionKey);
            onSectionDragOver(e, sectionKey);
          }}
          onDragLeave={() => {
            onSectionDragLeave(sectionKey);
            setDragOverSection(null);
          }}
          onDrop={(e) => {
            setDragOverSection(null);
            onSectionDrop(e, scopeType);
          }}
          data-testid={`${sectionKey}-section-drop-zone`}
        >
          {folders.map(renderFolder)}

          {volumes.map((volume) => (
            <VolumeItem
              key={`${sectionKey}-${volume.volumeType}-${volume.id}`}
              volume={volume}
              isSelected={
                appliedSavedVolume?.id === volume.id &&
                appliedSavedVolume?.volumeType === volume.volumeType
              }
              subtitle={
                volume.volumeType === 'advancedSearch' ||
                volume.volumeType === 'aiSearch'
                  ? 'AI Search'
                  : volume.volumeType === 'filterSet'
                    ? 'All Claims'
                    : 'Encounters'
              }
              onSelect={() => onSelect(buildSelectPayload(volume))}
              onEdit={(e) =>
                onEdit(e, { id: volume.id, label: volume.label, volumeType: volume.volumeType })
              }
              onDelete={(e) =>
                onDelete(e, {
                  id: volume.id,
                  label: volume.label,
                  volumeType: volume.volumeType,
                })
              }
              onReactivate={(e) =>
                onReactivate(e, { id: volume.id, label: volume.label })
              }
              onDragStart={(e) => onDragStart(e, volume)}
              onDragEnd={onDragEnd}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default VolumeSection;
